import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { filterActiveMasters } from "../utils/masterFilters";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const WagesSlipReport = () => {
  const dispatch = useDispatch();

  // Setup session state from localStorage
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // Lists & loader states
  const [firmList, setFirmList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [deptList, setDeptList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDept, setSelectedDept] = useState("");

  // Sync session changes from storage and sessionChange events
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, []);

  // Fetch initial masters
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
    Fn_FillListData(dispatch, setDeptList, "gridData", `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/Id/0`);
  }, [dispatch]);

  // Load report data when inputs change
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear, selectedDept, searchTerm);
    } else {
      setReportData(null);
    }
  }, [sessionFirm, selectedMonth, selectedYear, selectedDept, searchTerm]);

  const loadReportData = async (firmId, monthName, year, departmentId, searchQuery) => {
    setIsLoading(true);
    try {
      if (monthName && year) {
        const fd = new FormData();
        fd.append("F_FirmMaster", firmId);
        fd.append("PayrollMonth", monthName);
        fd.append("PayrollYear", year);
        fd.append("F_DepartmentMaster", departmentId || 0);
        fd.append("EmployeeSearch", searchQuery || "");

        // Fetching Wages Slip data
        const records = await Fn_GetReport(
          dispatch,
          () => {},
          "data",
          "Getwagesslip/0/token",
          { arguList: { id: 0, formData: fd } },
          true
        );

        let parsedData = null;
        if (records && records.length > 0) {
          const firstKey = Object.keys(records[0])[0];
          if (firstKey !== undefined && firstKey !== null) {
            const jsonString = records.map(row => row[firstKey] || "").join("");
            if (jsonString) {
              const rawObj = JSON.parse(jsonString);
              parsedData = rawObj.data || rawObj;
            }
          }
        }
        setReportData(parsedData);
      } else {
        setReportData(null);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getFirmPfNo = () => {
    if (reportData?.FirmDetails?.PFNo) return reportData.FirmDetails.PFNo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  const getFirmEsiNo = () => {
    if (reportData?.FirmDetails?.ESINo) return reportData.FirmDetails.ESINo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.ESINo || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  const getFirmAddress = () => {
    if (reportData?.FirmDetails?.OfficeAddress) return reportData.FirmDetails.OfficeAddress;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.OfficeAddress || mainBranch.CoAddress || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  // Mapped list of slips
  const getSlips = () => {
    if (!reportData || !reportData.Records) return [];
    const query = searchTerm.toLowerCase();

    return reportData.Records.map((rec, index) => {
      // Search term filtering
      const matchSearch = (rec.EmployeeName || rec.Name)?.toLowerCase().includes(query) || 
                          (rec.FatherName)?.toLowerCase().includes(query) || 
                          (rec.SerialNo || rec.AcNo || rec.EmpNo || String(rec.Sno))?.toLowerCase().includes(query);
      if (!matchSearch) return null;

      return {
        sno: index + 1,
        empNo: rec.EmpNo || rec.SerialNo || "",
        name: (rec.EmployeeName || rec.Name || "").toUpperCase(),
        fatherName: (rec.FatherName || "").toUpperCase(),
        department: rec.DepartmentName || "STAFF",
        designation: rec.Designation || "STAFF",
        // Attendance
        wd: parseFloat(rec.WD || rec.WorkedDay || 0),
        wh: parseFloat(rec.WH || rec.WeeklyHoliday || 0),
        lw: parseFloat(rec.LW || rec.Absent || 0),
        fh: parseFloat(rec.FH || rec.FestiveHoliday || 0),
        cl: parseFloat(rec.CL || 0),
        ml: parseFloat(rec.ML || 0),
        pd: parseFloat(rec.PaidDays || rec.PaidDay || 0),
        pfNo: rec.PFNo || "N/A",
        // Rates & Earnings
        basicRate: parseFloat(rec.BasicRate || 0),
        conRate: parseFloat(rec.CONRate || rec.ConveyanceRate || 0),
        earnedBasic: parseFloat(rec.BasicEarned || rec.Wages || 0),
        earnedHRA: parseFloat(rec.HRAEarned || rec.CalculatedAllowance1 || 0),
        arrear: parseFloat(rec.Arrear || 0),
        daRate: parseFloat(rec.DARate || 0),
        esiNo: rec.ESINo || "N/A",
        cardNo: rec.EmployeeCode || rec.CardNo || "N/A",
        // Deductions
        esi: parseFloat(rec.ESIDeduction || rec.ESI || 0),
        pf: parseFloat(rec.PFDeduction || rec.PF || 0),
        pension: parseFloat(rec.PensionDeduction || rec.Pension || 0),
        advance: parseFloat(rec.AdvanceDeduction || rec.LoanAdv || 0),
        tds: parseFloat(rec.TDSDeduction || rec.TDS || 0),
        // Totals
        grossWages: parseFloat(rec.GrossWages || rec.GrossAmount || 0),
        totalDed: parseFloat(rec.TotalDed || rec.TotalDeduction || 0),
        netPay: parseFloat(rec.NetPay || rec.NetPayment || 0)
      };
    }).filter(Boolean);
  };

  const slips = getSlips();

  // Calculations for report totals
  const totalEarnedBasic = slips.reduce((sum, s) => sum + s.earnedBasic, 0);
  const totalEarnedHRA = slips.reduce((sum, s) => sum + s.earnedHRA, 0);
  const totalArrear = slips.reduce((sum, s) => sum + s.arrear, 0);
  const totalEsi = slips.reduce((sum, s) => sum + s.esi, 0);
  const totalPf = slips.reduce((sum, s) => sum + s.pf, 0);
  const totalGross = slips.reduce((sum, s) => sum + s.grossWages, 0);
  const totalDeductions = slips.reduce((sum, s) => sum + s.totalDed, 0);
  const totalNetPay = slips.reduce((sum, s) => sum + s.netPay, 0);

  // PDF Export conformed to WAGESSLIP.pdf layout
  const exportToPDF = () => {
    if (slips.length === 0) {
      alert("No data available to export.");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");
    
    slips.forEach((slip, idx) => {
      // 4 slips per page
      const pageIndex = idx % 4;
      if (idx > 0 && pageIndex === 0) {
        pdf.addPage();
      }

      const startY = pageIndex * 70 + 8;

      // Header info
      pdf.setFontSize(9);
      pdf.setFont("Helvetica", "bold");
      pdf.text(sessionFirm?.Name?.toUpperCase() || "", 14, startY);
      pdf.setFontSize(7.5);
      pdf.setFont("Helvetica", "normal");
      pdf.text(getFirmAddress(), 14, startY + 4);
      pdf.setFont("Helvetica", "bold");
      pdf.text(`WAGES/SALARY SLIP FOR THE MONTH OF ${(selectedMonth || "").toUpperCase()} ${selectedYear}`, 14, startY + 8);

      pdf.setFontSize(7.5);
      pdf.text(`Emp No: ${slip.empNo}  NAME: ${slip.name}  FATHER/HUSBAND'S: ${slip.fatherName}`, 14, startY + 13);
      pdf.text(`Department: ${slip.department}  DESIGNATION: ${slip.designation}`, 14, startY + 17);

      // Draw conjoined details inside a table
      autoTable(pdf, {
        body: [
          [
            `WD: ${slip.wd.toFixed(2)}\nLW: ${slip.lw.toFixed(2)}\nCL: ${slip.cl.toFixed(2)}\nPD: ${slip.pd.toFixed(2)}\nPF/UAN: ${slip.pfNo}`,
            `WH: ${slip.wh.toFixed(2)}\nFH: ${slip.fh.toFixed(2)}\nML: ${slip.ml.toFixed(2)}`,
            `Rate: ${slip.basicRate.toFixed(2)}\nWages: ${slip.earnedBasic.toFixed(2)}\nArrear: ${slip.arrear.toFixed(2)}\nD.A.: ${slip.daRate.toFixed(2)}\nHRA: ${slip.earnedHRA.toFixed(2)}\nE.S.I. No.: ${slip.esiNo}`,
            `CON: ${slip.conRate.toFixed(2)}`,
            `ESI: ${slip.esi.toFixed(2)}\nPF: ${slip.pf.toFixed(2)}\nPen: ${slip.pension.toFixed(2)}\nEmp Code: ${slip.cardNo}`,
            `Advance: ${slip.advance.toFixed(2)}\nTDS: ${slip.tds.toFixed(2)}\nGROSS: ${slip.grossWages.toFixed(2)}\nTOTAL DED: ${slip.totalDed.toFixed(2)}\nNET PAY: ${slip.netPay.toFixed(2)}`
          ]
        ],
        startY: startY + 20,
        margin: { left: 14, right: 14 },
        styles: { fontSize: 6.5, cellPadding: 1, lineColor: [0, 0, 0], lineWidth: 0.05 },
        columnStyles: {
          0: { cellWidth: 32 },
          1: { cellWidth: 22 },
          2: { cellWidth: 42 },
          3: { cellWidth: 22 },
          4: { cellWidth: 32 },
          5: { cellWidth: 32 }
        }
      });

      const finalY = pdf.previousAutoTable.finalY || (startY + 48);
      pdf.setFont("Helvetica", "bold");
      pdf.text("EMPLOYER'S SIGNATURE", 14, finalY + 4);
      pdf.text("EMPLOYEE'S SIGNATURE", 150, finalY + 4);

      // Dash separator line
      pdf.setLineDash([1, 1]);
      pdf.line(14, finalY + 6, 196, finalY + 6);
      pdf.setLineDash([]); // Reset dash
    });

    pdf.save(`Wages_Slips_${selectedMonth}_${selectedYear}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-wages-slips-container, #print-wages-slips-container * {
        visibility: visible !important;
      }
      #print-wages-slips-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff;
        color: #000;
        font-family: Arial, sans-serif;
      }
      .no-print {
        display: none !important;
      }
      .print-slip-block {
        width: 100%;
        height: 24%;
        border-bottom: 1px dashed #000;
        padding: 10px 0;
        box-sizing: border-box;
        page-break-inside: avoid;
      }
      .print-page-break {
        page-break-after: always;
        height: 0;
      }
      .slip-print-table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 5px;
      }
      .slip-print-table td {
        border: 1px solid #000;
        padding: 3px;
        font-size: 8px;
        vertical-align: top;
        line-height: 1.3;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="Wages Slip Report" motherMenu="Reports" pageContent="Salary Slips Generation" />
      </div>

      {/* Filters Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Department */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Department</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
              >
                <option value="">All Departments</option>
                {filterActiveMasters(deptList, selectedDept).map((d) => (
                  <option key={d.Id} value={d.Id}>{d.Name}</option>
                ))}
              </select>
            </Col>

            {/* Month */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Search */}
            <Col lg={2} md={12}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Employee</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Name, UAN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm**, **Month**, and **Year** to load the report.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px", overflow: "hidden" }}>
              <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom py-3 px-4">
                <div>
                  <h5 className="mb-0 text-dark fw-bold fs-16">Wages/Salary Slips</h5>
                  <small className="text-muted">
                    Total Slips: <strong>{slips.length}</strong>
                  </small>
                </div>
                <div>
                  <Button variant="danger" size="sm" className="me-2" onClick={exportToPDF}>PDF Download</Button>
                  <Button variant="primary" size="sm" onClick={() => window.print()}>Print Slips</Button>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Generating Wages Slips Report...</div>
                  </div>
                ) : slips.length === 0 ? (
                  <div className="text-center text-muted p-5">No slips available.</div>
                ) : (
                  <div style={{ overflowX: "auto", display: "block", width: "100%" }}>
                    <table className="table table-bordered table-striped table-hover mb-0 align-middle text-nowrap" style={{ fontSize: "11px", minWidth: "1250px", width: "100%" }}>
                      <thead className="table-light text-secondary fw-semibold">
                        <tr>
                          <th className="text-center" style={{ width: "40px" }}>Sn</th>
                          <th>Employee / Father Name</th>
                          <th>Emp No & ESI/PF</th>
                          <th>Dept / Designation</th>
                          <th className="text-center">WD / PD</th>
                          <th className="text-end">Basic Rate</th>
                          <th className="text-end">Earned Basic</th>
                          <th className="text-end">Earned HRA</th>
                          <th className="text-end">Arrear</th>
                          <th className="text-end">ESI Ded.</th>
                          <th className="text-end">PF Ded.</th>
                          <th className="text-end">Gross</th>
                          <th className="text-end">Total Ded.</th>
                          <th className="text-end text-success fw-bold">Net Pay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {slips.map((slip, idx) => (
                          <tr key={slip.empNo}>
                            <td className="text-center text-muted">{idx + 1}</td>
                            <td>
                              <span className="fw-semibold text-dark">{slip.name}</span>
                              <div className="text-muted fs-11">F: {slip.fatherName}</div>
                            </td>
                            <td>
                              <span className="text-dark fw-medium">No: {slip.empNo}</span>
                              <div className="text-muted fs-11">ESI: {slip.esiNo} | PF: {slip.pfNo}</div>
                            </td>
                            <td>
                              <span className="text-dark">{slip.department}</span>
                              <div className="text-muted fs-11">{slip.designation}</div>
                            </td>
                            <td className="text-center">
                              <span className="fw-semibold text-dark">{slip.wd} / {slip.pd}</span>
                              <div className="text-muted fs-11">LW (Absent): {slip.lw}</div>
                            </td>
                            <td className="text-end text-dark">₹{slip.basicRate.toFixed(2)}</td>
                            <td className="text-end text-dark">₹{slip.earnedBasic.toFixed(2)}</td>
                            <td className="text-end text-dark">₹{slip.earnedHRA.toFixed(2)}</td>
                            <td className="text-end text-dark">₹{slip.arrear.toFixed(2)}</td>
                            <td className="text-end text-dark">₹{slip.esi.toFixed(2)}</td>
                            <td className="text-end text-dark">₹{slip.pf.toFixed(2)}</td>
                            <td className="text-end text-dark fw-semibold">₹{slip.grossWages.toFixed(2)}</td>
                            <td className="text-end text-danger fw-semibold">₹{slip.totalDed.toFixed(2)}</td>
                            <td className="text-end text-success fw-bold">₹{slip.netPay.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr className="fw-bold" style={{ borderTop: "2px solid #dee2e6" }}>
                          <td colSpan={5} className="text-end fw-bold">Total:</td>
                          <td className="text-end">-</td>
                          <td className="text-end">₹{totalEarnedBasic.toFixed(2)}</td>
                          <td className="text-end">₹{totalEarnedHRA.toFixed(2)}</td>
                          <td className="text-end">₹{totalArrear.toFixed(2)}</td>
                          <td className="text-end">₹{totalEsi.toFixed(2)}</td>
                          <td className="text-end">₹{totalPf.toFixed(2)}</td>
                          <td className="text-end text-dark">₹{totalGross.toFixed(2)}</td>
                          <td className="text-end text-danger">₹{totalDeductions.toFixed(2)}</td>
                          <td className="text-end text-success">₹{totalNetPay.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Print View Layout */}
          <div id="print-wages-slips-container" className="d-none">
            {slips.map((slip, index) => (
              <Fragment key={slip.empNo}>
                <div className="print-slip-block">
                  <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "10px" }}>
                    {sessionFirm?.Name?.toUpperCase()}
                  </div>
                  <div style={{ textAlign: "center", fontSize: "8px" }}>
                    {getFirmAddress()}
                  </div>
                  <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "9px", margin: "4px 0" }}>
                    WAGES/SALARY SLIP FOR THE MONTH OF {selectedMonth?.toUpperCase()} {selectedYear}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "8px", marginBottom: "4px", fontWeight: "bold" }}>
                    <div>Emp No : {slip.empNo} &nbsp;&nbsp; NAME : {slip.name}</div>
                    <div>FATHER/HUSBAND'S : {slip.fatherName}</div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "8px", marginBottom: "4px", fontWeight: "bold" }}>
                    <div>Department : {slip.department}</div>
                    <div>DESIGNATION : {slip.designation}</div>
                  </div>

                  <table className="slip-print-table">
                    <tbody>
                      <tr>
                        <td style={{ width: "16%" }}>
                          WD: {slip.wd.toFixed(2)}<br />
                          LW: {slip.lw.toFixed(2)}<br />
                          CL: {slip.cl.toFixed(2)}<br />
                          PD: {slip.pd.toFixed(2)}<br />
                          PF No / UAN: {slip.pfNo}
                        </td>
                        <td style={{ width: "12%" }}>
                          WH: {slip.wh.toFixed(2)}<br />
                          FH: {slip.fh.toFixed(2)}<br />
                          ML: {slip.ml.toFixed(2)}
                        </td>
                        <td style={{ width: "24%" }}>
                          Rate: {slip.basicRate.toFixed(2)}<br />
                          Wages: {slip.earnedBasic.toFixed(2)}<br />
                          Arrear: {slip.arrear.toFixed(2)}<br />
                          D.A.: {slip.daRate.toFixed(2)}<br />
                          HRA: {slip.earnedHRA.toFixed(2)}<br />
                          E.S.I. No.: {slip.esiNo}
                        </td>
                        <td style={{ width: "12%" }}>
                          CON: {slip.conRate.toFixed(2)}
                        </td>
                        <td style={{ width: "18%" }}>
                          ESI : {slip.esi.toFixed(2)}<br />
                          PF : {slip.pf.toFixed(2)}<br />
                          Pen : {slip.pension.toFixed(2)}<br />
                          Emp Code: {slip.cardNo}
                        </td>
                        <td style={{ width: "18%" }}>
                          Advance : {slip.advance.toFixed(2)}<br />
                          TDS: {slip.tds.toFixed(2)}<br />
                          <strong>GROSS WAGES: {slip.grossWages.toFixed(2)}</strong><br />
                          <strong>TOTAL DED: {slip.totalDed.toFixed(2)}</strong><br />
                          <strong>NET PAY: {slip.netPay.toFixed(2)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontSize: "8px", fontWeight: "bold" }}>
                    <div>EMPLOYER'S SIGNATURE</div>
                    <div>EMPLOYEE'S SIGNATURE</div>
                  </div>
                </div>
                {(index + 1) % 4 === 0 && <div className="print-page-break" />}
              </Fragment>
            ))}
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default WagesSlipReport;
